import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    Button,
    Tabs,
    Tab,
    Typography,
    Grid,
    useTheme,
    useMediaQuery,
    Link,
    Tooltip
} from "@mui/material";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import LaunchIcon from "@mui/icons-material/Launch";
import ProfileTab from "./ProfileTab";
import AvailabilityTab from "./AvailabilityTab";
import BlockedTimeTab from "./BlockedTimeTab";
import { useAtom } from "jotai";
import {
    selectedUserIdAtom,
    selectedUserAtom,
    userModalTabValue,
    googleIntegrationCalInfoAtom,
    updateGoogleIntegrationCalInfoAtom
} from "../../atoms/usersTable";
import { useQuery } from "@tanstack/react-query";
import { graphqlOperation, graphql } from "../../modules/AmplifyServices";
import {
    skillByCompanyCustom,
    serviceTypeByCompanyCustom
} from "../../queries/ProviderFormQueries";
import {
    mbxUserAtom,
    isMbxUserProvider,
    allSkillsAtom,
    allServicesAtom,
    confirmationFunctionAtom
} from "../../atoms/atoms";
import { useNavigate } from "react-router-dom";
import GoogleCalSVG from "../../images/google-calendar.svg";
import { Box } from "@mui/system";
import {
    GOOGLE_CAL_INTEGRATION_STATUS_SYNC_FAILED,
    GOOGLE_CAL_INTEGRATION_STATUS_SYNCED
} from "../../utils/Constants";

const UserProfileModal = ({ open, onClose }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true
    });
    const [mbxUser] = useAtom(mbxUserAtom);
    const [isProviderLoggedIn] = useAtom(isMbxUserProvider);
    const { companyId } = mbxUser;
    const [tabValue, setTabValue] = useAtom(userModalTabValue);
    const [, setSelectedUserId] = useAtom(selectedUserIdAtom);
    const [, setAllServices] = useAtom(allServicesAtom);
    const [, setAllSkills] = useAtom(allSkillsAtom);
    const [, setGoogleCalInfo] = useAtom(updateGoogleIntegrationCalInfoAtom);
    const [selectedUser] = useAtom(selectedUserAtom);
    const [googleCalInfo] = useAtom(googleIntegrationCalInfoAtom);
    const [, setConfirmationFunction] = useAtom(confirmationFunctionAtom);
    const [initialFirstName, setInitialFirstName] = useState("");
    const [initialLastName, setInitialLastName] = useState("");

    // Add Escape key listener
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                handleClose();
            }
        };

        if (open) {
            window.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [open]);

    const handleChangeTab = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleClose = () => {
        onClose();
        setTimeout(() => {
            //Delay added so when modal closes User doesnt see all states being reset
            setTabValue(0);
            setInitialFirstName("");
            setInitialLastName("");
            setSelectedUserId(mbxUser.id);
            setGoogleCalInfo(null);
        }, 100);
        setConfirmationFunction();
    };

    const handleGoToCalendar = () => {
        const path = `/calendar?pIds=${selectedUser.provider.id}`;
        navigate(path);
        handleClose();
    };

    useQuery({
        queryKey: ["get-services"],
        queryFn: async () => {
            try {
                let {
                    data: {
                        serviceTypeByCompany: { items }
                    }
                } = await graphql(
                    graphqlOperation(serviceTypeByCompanyCustom, {
                        companyId: companyId,
                        filter: {
                            deleted: { ne: true },
                            isBundledService: { ne: true }
                        },
                        limit: 500
                    })
                );
                return items || null;
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (servicesData) => {
            setAllServices(servicesData);
        }
    });
    useQuery({
        queryKey: ["get-skills"],
        queryFn: async () => {
            try {
                let {
                    data: {
                        skillByCompany: { items }
                    }
                } = await graphql(
                    graphqlOperation(skillByCompanyCustom, {
                        companyId: companyId,
                        filter: {
                            deleted: { ne: true }
                        },
                        limit: 500
                    })
                );
                return items || null;
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (SkillsData) => {
            setAllSkills(SkillsData);
        }
    });

    const getTitle = () => {
        if (!isDesktop) {
            switch (tabValue) {
                case 0:
                    return "Profile";
                case 1:
                    return "Availability";
                case 2:
                    return "Blocked time";
                default:
                    return "Add user";
            }
        }
        if (selectedUser) {
            return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2.25 }}>
                    {`${selectedUser?.firstname} ${selectedUser?.lastname}`}
                    {googleCalInfo?.status &&
                        googleCalInfo?.status !==
                            GOOGLE_CAL_INTEGRATION_STATUS_SYNC_FAILED && (
                            <Tooltip
                                title={
                                    googleCalInfo?.status !==
                                    GOOGLE_CAL_INTEGRATION_STATUS_SYNCED
                                        ? "Google Calendar syncing"
                                        : "Google Calendar connected"
                                }
                                placement="top"
                            >
                                <img
                                    src={GoogleCalSVG}
                                    alt="google-cal"
                                    style={{ width: 18, height: 18 }}
                                />
                            </Tooltip>
                        )}
                    {isProviderLoggedIn &&
                        selectedUser?.provider?.permalink &&
                        tabValue === 0 && (
                            <Tooltip
                                title={"Go to personal booking link"}
                                placement="top"
                            >
                                <Link
                                    sx={{
                                        "&:hover": {
                                            color: theme.palette.primary.main
                                        },
                                        lineHeight: "0px"
                                    }}
                                    target="_blank"
                                    rel="noopener"
                                    href={`https://${selectedUser?.company?.subdomain}/providers/${selectedUser?.provider?.permalink}`}
                                >
                                    <LaunchIcon
                                        sx={{ fontSize: "20px" }}
                                        viewBox="2 2 20 20"
                                    />
                                </Link>
                            </Tooltip>
                        )}
                </Box>
            );
        }
        if (initialFirstName && initialLastName) {
            return `${initialFirstName} ${initialLastName}`;
        }
        return "Add user";
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
            fullScreen={!isDesktop}
        >
            <DialogTitle
                flexDirection={"column"}
                sx={{
                    paddingBottom: selectedUser ? "0px" : "36px"
                }}
            >
                <Grid container gap={2} flexWrap={"nowrap"}>
                    <Typography noWrap variant="h3" lineHeight={1.1}>
                        {getTitle()}
                    </Typography>
                </Grid>
                {selectedUser && isDesktop && (
                    <Grid
                        container
                        justifyContent={
                            selectedUser ? "space-between" : "flex-end"
                        }
                        alignItems="center"
                    >
                        <Tabs value={tabValue} onChange={handleChangeTab}>
                            <Tab label="Profile" />
                            <Tab label="Availability" />
                            <Tab label="Blocked Time" />
                        </Tabs>
                        {!isProviderLoggedIn && isDesktop && (
                            <Button
                                variant="text"
                                sx={{
                                    gap: "8px"
                                }}
                                onClick={handleGoToCalendar}
                            >
                                <Typography
                                    variant="button"
                                    sx={{
                                        textTransform: "none",
                                        lineHeight: "16px"
                                    }}
                                >
                                    {`Go to calendar`}
                                </Typography>
                                <EventRoundedIcon color="primary" />
                            </Button>
                        )}
                    </Grid>
                )}
            </DialogTitle>

            {tabValue === 0 && (
                <ProfileTab
                    setInitialFirstName={setInitialFirstName}
                    setInitialLastName={setInitialLastName}
                    onClose={handleClose}
                />
            )}
            {tabValue === 1 && <AvailabilityTab onClose={handleClose} />}
            {tabValue === 2 && <BlockedTimeTab onClose={handleClose} />}
        </Dialog>
    );
};

export default UserProfileModal;
